import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { Hostel } from '@/api/types';
import { TcModal, SignaturePad } from '@/components/SignatureCapture';
import { Building2, CheckCircle2 } from 'lucide-react';
import { TENANT_STATUS_COLORS, fmt, useTenantPortalGuard, HostelSelector } from './tenantPortalShared';

// ── Required Agreements section (inline in profile) ───────────────────────
function RequiredAgreements({ hostel, onAccepted }: { hostel: Hostel | null; onAccepted: () => void }) {
  const { t } = useTranslation();
  const [detailsCorrect, setDetailsCorrect] = useState(false);
  const [tcAccepted, setTcAccepted]         = useState(false);
  const [signature, setSignature]           = useState<string | null>(null);
  const [showTc, setShowTc]                 = useState(false);

  const canSubmit = detailsCorrect && tcAccepted && !!signature;

  const acceptMutation = useMutation({
    mutationFn: () => client.post('/api/tenant-portal/accept-terms', { signature }),
    onSuccess: onAccepted,
  });

  return (
    <>
      {showTc && <TcModal hostel={hostel} onClose={() => setShowTc(false)} />}

      <div className="border-t-2 border-dashed border-amber-200 pt-6 space-y-4">
        <p className="text-base font-bold text-gray-900">{t('tenant.requiredAgreements', 'Required Agreements')}</p>

        {/* Checkbox 1 */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={detailsCorrect}
            onChange={e => setDetailsCorrect(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-amber-600 cursor-pointer shrink-0"
          />
          <span className="text-sm text-gray-700">
            {t('tenant.detailsCorrect', 'The above mentioned details are correct.')}
          </span>
        </label>

        {/* Checkbox 2 */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={tcAccepted}
            onChange={e => setTcAccepted(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-amber-600 cursor-pointer shrink-0"
          />
          <span className="text-sm text-gray-700">
            {t('tenant.agreeToThe', 'I have read and agree to the')}{' '}
            <button
              type="button"
              onClick={e => { e.preventDefault(); setShowTc(true); }}
              className="text-amber-600 underline underline-offset-2 hover:text-amber-800 font-medium"
            >
              {t('tenant.termsAndConditions', 'Terms & Conditions')}
            </button>
            {' '}{t('tenant.andPrivacyPolicy', 'and Privacy Policy of hostel management.')}
          </span>
        </label>

        {/* Signature box */}
        <SignaturePad onChange={setSignature} />

        <button
          disabled={!canSubmit || acceptMutation.isPending}
          onClick={() => acceptMutation.mutate()}
          className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white font-semibold py-2.5 rounded-xl transition text-sm"
        >
          {acceptMutation.isPending ? t('tenant.saving', 'Saving…') : t('common.submit')}
        </button>
        {acceptMutation.isError && (
          <p className="text-red-600 text-sm text-center">{t('common.genericError', 'Something went wrong. Please try again.')}</p>
        )}
      </div>
    </>
  );
}

function Field({ label, value }: { label: string; value?: string | number | null }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-sm text-gray-900 break-words">{value}</p>
    </div>
  );
}

// ── Main dashboard (Home) ───────────────────────────────────────────────────
export function TenantDashboard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const portal = useTenantPortalGuard();

  const { data: hostelInfo } = useQuery<Hostel | null>({
    queryKey: ['tenant-portal-hostel-rules'],
    queryFn: () => client.get('/api/tenant-portal/hostel-rules').then(r => r as unknown as Hostel | null),
    enabled: !('guard' in portal),
  });

  if ('guard' in portal) return portal.guard;
  const { tenant, activeTenants, tenantIdx, setSelectedTenantIdx } = portal;

  return (
    <div className="max-w-3xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-xl shrink-0">
          {tenant.fullName.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">{tenant.fullName}</h1>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TENANT_STATUS_COLORS[tenant.status] ?? 'bg-gray-100'}`}>
              {tenant.status.replace('_', ' ')}
            </span>
            {tenant.hostelName && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Building2 size={11} className="text-amber-500" />
                {tenant.hostelName}
              </span>
            )}
            {tenant.roomNumber && (
              <span className="text-xs text-gray-400">{t('common.room')} {tenant.roomNumber}{tenant.bedLabel ? ` · ${t('common.bed')} ${tenant.bedLabel}` : ''}</span>
            )}
          </div>
        </div>
      </div>

      {/* Hostel selector — only if tenant has more than one active admission */}
      <HostelSelector activeTenants={activeTenants} tenantIdx={tenantIdx} onChange={setSelectedTenantIdx} />

      {/* Profile info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          {tenant.hostelName && <Field label={t('tenant.hostel', 'Hostel')}            value={tenant.hostelName} />}
          <Field label={t('tenant.fullNameLabel', 'Full Name')}         value={tenant.fullName} />
          <Field label={t('common.phone')}             value={tenant.phone} />
          <Field label={t('common.email')}             value={tenant.email} />
          <Field label={t('tenant.occupation', 'Occupation')}        value={tenant.occupation} />
          <Field label={t('tenant.monthlyRent')}      value={tenant.monthlyRent    != null ? fmt(tenant.monthlyRent)    : undefined} />
          <Field label={t('tenant.securityDeposit', 'Security Deposit')}  value={tenant.securityDeposit != null ? fmt(tenant.securityDeposit) : undefined} />
          <Field label={t('tenant.joiningDate')}      value={tenant.joiningDate} />
          <Field label={t('tenant.expectedCheckout', 'Expected Checkout')} value={tenant.expectedCheckoutDate} />
        </div>

        {/* Signed badge */}
        {tenant.termsAccepted && (
          <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
            <CheckCircle2 size={14} className="text-green-600" />
            {t('tenant.signedAndAgreed', 'Signed & agreed')}
            {tenant.termsAcceptedAt && t('tenant.onDateSuffix', ' on {{date}}', { date: new Date(tenant.termsAcceptedAt).toLocaleDateString('en-IN') })}
          </div>
        )}

        {/* Required agreements (shown only when not yet signed) */}
        {!tenant.termsAccepted && (
          <RequiredAgreements
            hostel={hostelInfo ?? null}
            onAccepted={() => void qc.invalidateQueries({ queryKey: ['tenant-portal-me'] })}
          />
        )}
      </div>
    </div>
  );
}
